import { useLocaleContext } from "@app/contexts/LocaleContext";

/**
 * Terms of use, in French and in English. The English text is a translation for the physicians
 * reading the platform in English (and in German or Spanish, which have no version of their own);
 * the French one is the reference. Any change to one must be carried to the other.
 */
export default function CGUS() {
  const { locale } = useLocaleContext();
  return locale === "fr" ? <CgusFr /> : <CgusEn />;
}

function CgusFr() {
  return (
    <div className="max-h-full overflow-auto p-10">
      <h1 className="-ml-2 mb-2 text-2xl font-bold">
        Conditions Générales d'Utilisation (CGU) - MAGE-X
      </h1>
      <p>
        <strong>Date de mise à jour :</strong> 24/11/2024
      </p>

      <h2>1. Préambule</h2>
      <p>
        Les présentes Conditions Générales d'Utilisation (CGU) encadrent l'accès
        et l'utilisation de la plateforme MAGE-X (ci-après « la Plateforme »),
        réservée exclusivement aux professionnels de santé dûment certifiés. En
        créant un compte, vous acceptez les présentes CGU et vous engagez à
        respecter l'éthique professionnelle ainsi que les lois en vigueur,
        notamment le respect du secret médical.
      </p>

      <h2>2. Objet de la Plateforme</h2>
      <p>La Plateforme a pour objectif de permettre :</p>
      <ul>
        <li>La mise en relation des professionnels de santé ;</li>
        <li>Le partage de connaissances médicales et scientifiques ;</li>
        <li>
          Les discussions sur des cas médicaux anonymisés à des fins éducatives
          ou collaboratives.
        </li>
      </ul>
      <p>
        <strong>Attention :</strong> Toute mention directe ou indirecte
        permettant d'identifier un patient est strictement interdite.
      </p>

      <h2>3. Conditions d'accès</h2>
      <p>
        <strong>Réservé aux professionnels de santé :</strong> L'inscription est
        exclusivement ouverte aux médecins et professionnels de santé habilités.
        Un justificatif de qualification (n° RPPS, diplôme, etc.) peut être
        exigé pour valider votre compte.
      </p>
      <p>
        <strong>Authenticité des informations :</strong> Vous vous engagez à
        fournir des informations exactes et à jour lors de l'inscription.
      </p>
      <p>
        <strong>Âge minimum :</strong> Vous devez être âgé d’au moins 18 ans
        pour utiliser la Plateforme.
      </p>

      <h2>4. Règles d'utilisation</h2>
      <p>
        En utilisant la Plateforme, vous vous engagez à respecter les règles
        suivantes :
      </p>
      <h3>4.1. Éthique professionnelle</h3>
      <ul>
        <li>
          Ne pas publier ou partager des contenus contraires à la déontologie
          médicale ou à l’éthique professionnelle.
        </li>
        <li>Maintenir une conduite respectueuse envers les autres membres.</li>
      </ul>
      <h3>4.2. Respect du secret médical</h3>
      <ul>
        <li>
          Interdiction stricte de mentionner des noms ou toute information
          permettant d’identifier un patient.
        </li>
        <li>
          Tout cas médical discuté sur la Plateforme doit être anonymisé (pas de
          nom, de date précise, de localisation identifiable, ou de toute autre
          information personnelle).
        </li>
      </ul>
      <h3>4.3. Contenus partagés</h3>
      <ul>
        <li>
          Ne pas publier de contenu diffamatoire, injurieux, discriminatoire ou
          à caractère violent.
        </li>
        <li>
          Ne pas partager de fausses informations médicales ou non vérifiées.
        </li>
      </ul>

      <h2>5. Propriété intellectuelle</h2>
      <p>
        Les contenus publiés sur la Plateforme par les membres restent la
        propriété de leurs auteurs respectifs.
      </p>
      <p>
        Vous accordez à MAGE-X une licence non exclusive pour utiliser,
        héberger, et afficher vos contenus dans le cadre du fonctionnement de la
        Plateforme.
      </p>
      <p>
        Tout contenu appartenant à la Plateforme (logos, interfaces, codes,
        etc.) est protégé par des droits de propriété intellectuelle et ne peut
        être utilisé sans autorisation préalable.
      </p>

      <h2>6. Responsabilité</h2>
      <h3>6.1. Responsabilité de l'utilisateur</h3>
      <p>
        Vous êtes seul responsable des contenus que vous publiez ou partagez sur
        la Plateforme.
      </p>
      <p>
        Vous vous engagez à ne pas enfreindre les lois, notamment celles
        relatives au secret médical et à la confidentialité des données
        personnelles.
      </p>
      <h3>6.2. Limitation de responsabilité de la Plateforme</h3>
      <p>
        La Plateforme ne garantit pas l’exactitude des informations publiées par
        les utilisateurs.
      </p>
      <p>
        En cas de violation des CGU, la Plateforme se réserve le droit de
        suspendre ou supprimer un compte sans préavis.
      </p>

      <h2>7. Données personnelles</h2>
      <p>
        Votre utilisation de la Plateforme implique la collecte et le traitement
        de certaines données personnelles, conformément à notre Politique de
        confidentialité. Ces données ne seront utilisées que pour assurer le
        fonctionnement de la Plateforme et en conformité avec le Règlement
        Général sur la Protection des Données (RGPD).
      </p>

      <h2>8. Sanctions</h2>
      <p>
        Tout manquement aux présentes CGU, notamment au respect du secret
        médical, pourra entraîner :
      </p>
      <ul>
        <li>Un avertissement.</li>
        <li>La suspension temporaire de votre compte.</li>
        <li>
          La suppression définitive de votre compte, sans possibilité de
          recours.
        </li>
        <li>
          Le signalement aux autorités compétentes en cas de violation grave.
        </li>
      </ul>

      <h2>9. Modification des CGU</h2>
      <p>
        Les présentes CGU peuvent être modifiées à tout moment pour s'adapter
        aux évolutions de la Plateforme ou aux obligations légales. Vous serez
        informé de toute modification et votre utilisation continue de la
        Plateforme vaudra acceptation des nouvelles CGU.
      </p>

      <h2>10. Loi applicable et juridiction compétente</h2>
      <p>
        Les présentes CGU sont régies par les lois en vigueur en <em>France</em>
        .
      </p>

      <h2>11. Contact</h2>
      <p>
        Pour toute question ou réclamation concernant ces CGU, vous pouvez nous
        contacter à <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
    </div>
  );
}

function CgusEn() {
  return (
    <div className="max-h-full overflow-auto p-10">
      <h1 className="-ml-2 mb-2 text-2xl font-bold">Terms of Use - MAGE-X</h1>
      <p>
        <strong>Last updated:</strong> 24 November 2024
      </p>
      <p>
        <em>
          This English version is a translation provided for convenience. In
          case of discrepancy, the French version prevails.
        </em>
      </p>

      <h2>1. Preamble</h2>
      <p>
        These Terms of Use govern access to and use of the MAGE-X platform
        (hereinafter « the Platform »), which is reserved exclusively for duly
        certified healthcare professionals. By creating an account, you accept
        these Terms of Use and undertake to abide by professional ethics and by
        the laws in force, in particular medical confidentiality.
      </p>

      <h2>2. Purpose of the Platform</h2>
      <p>The Platform aims to enable:</p>
      <ul>
        <li>Connecting healthcare professionals;</li>
        <li>Sharing medical and scientific knowledge;</li>
        <li>
          Discussing anonymised medical cases for educational or collaborative
          purposes.
        </li>
      </ul>
      <p>
        <strong>Warning:</strong> Any direct or indirect mention that would
        allow a patient to be identified is strictly prohibited.
      </p>

      <h2>3. Conditions of access</h2>
      <p>
        <strong>Reserved for healthcare professionals:</strong> Registration is
        open exclusively to physicians and authorised healthcare professionals.
        Proof of qualification (RPPS number, diploma, etc.) may be required to
        validate your account.
      </p>
      <p>
        <strong>Accuracy of information:</strong> You undertake to provide
        accurate and up-to-date information when registering.
      </p>
      <p>
        <strong>Minimum age:</strong> You must be at least 18 years old to use
        the Platform.
      </p>

      <h2>4. Rules of use</h2>
      <p>
        By using the Platform, you undertake to comply with the following rules:
      </p>
      <h3>4.1. Professional ethics</h3>
      <ul>
        <li>
          Do not publish or share content contrary to medical deontology or
          professional ethics.
        </li>
        <li>Behave respectfully towards other members.</li>
      </ul>
      <h3>4.2. Medical confidentiality</h3>
      <ul>
        <li>
          It is strictly forbidden to mention names or any information that
          would allow a patient to be identified.
        </li>
        <li>
          Any medical case discussed on the Platform must be anonymised (no
          name, precise date, identifiable location, or any other personal
          information).
        </li>
      </ul>
      <h3>4.3. Shared content</h3>
      <ul>
        <li>
          Do not publish defamatory, insulting, discriminatory or violent
          content.
        </li>
        <li>Do not share false or unverified medical information.</li>
      </ul>

      <h2>5. Intellectual property</h2>
      <p>
        Content published on the Platform by members remains the property of its
        respective authors.
      </p>
      <p>
        You grant MAGE-X a non-exclusive licence to use, host and display your
        content for the operation of the Platform.
      </p>
      <p>
        All content belonging to the Platform (logos, interfaces, code, etc.) is
        protected by intellectual property rights and may not be used without
        prior authorisation.
      </p>

      <h2>6. Liability</h2>
      <h3>6.1. User liability</h3>
      <p>
        You are solely responsible for the content you publish or share on the
        Platform.
      </p>
      <p>
        You undertake not to break the law, in particular the laws on medical
        confidentiality and on the protection of personal data.
      </p>
      <h3>6.2. Limitation of the Platform's liability</h3>
      <p>
        The Platform does not guarantee the accuracy of the information
        published by users.
      </p>
      <p>
        In the event of a breach of these Terms of Use, the Platform reserves
        the right to suspend or delete an account without notice.
      </p>

      <h2>7. Personal data</h2>
      <p>
        Your use of the Platform involves the collection and processing of
        certain personal data, in accordance with our Privacy Policy. This data
        will only be used to operate the Platform and in compliance with the
        General Data Protection Regulation (GDPR).
      </p>

      <h2>8. Sanctions</h2>
      <p>
        Any breach of these Terms of Use, in particular of medical
        confidentiality, may result in:
      </p>
      <ul>
        <li>A warning.</li>
        <li>The temporary suspension of your account.</li>
        <li>
          The permanent deletion of your account, with no right of appeal.
        </li>
        <li>
          A report to the competent authorities in the event of a serious
          breach.
        </li>
      </ul>

      <h2>9. Changes to the Terms of Use</h2>
      <p>
        These Terms of Use may be amended at any time to reflect changes to the
        Platform or to legal obligations. You will be informed of any change,
        and your continued use of the Platform will constitute acceptance of the
        new Terms of Use.
      </p>

      <h2>10. Governing law and jurisdiction</h2>
      <p>
        These Terms of Use are governed by the laws in force in <em>France</em>.
      </p>

      <h2>11. Contact</h2>
      <p>
        For any question or complaint about these Terms of Use, you can contact
        us at <a href="mailto:contact@mage-x.fr">contact@mage-x.fr</a>.
      </p>
    </div>
  );
}
